import { useEffect, useMemo, useRef, useState } from "react";
import { apiPost } from "../../services/api";

type LookupRange = {
  min: number;
  max: number;
  price: number;
};

export type CostCalculatorDto = {
  Id?: number;
  SessionId?: string;
  HasProvider: boolean;
  CurrentProviderPrice?: number;
  EstimatedMontlyLookups?: number;
  CurrentProvidersEstimatedCost?: number;
  OurPrice?: number;
  TotalEstimatedSavingsWithEAFPerMonth?: number;
  TotalEstimatedSavingsWithEAFPerAnnum?: number;
  CurrentProviderRenewalDate?: string;
  EmailAddress?: string;
  PhoneNumber?: string;
};

const lookupRanges: LookupRange[] = [
  { min: 1, max: 1000, price: 1.3 },
  { min: 1001, max: 2000, price: 1.3 },
  { min: 2001, max: 5000, price: 1.3 },
  { min: 5001, max: 10000, price: 1.3 },
  { min: 10001, max: 25000, price: 1.2 },
  { min: 25001, max: 50000, price: 1.15 },
  { min: 50001, max: 100000, price: 1.1 },
  { min: 100001, max: 500000, price: 0.9 },
];

const defaultExpiryDate = () => {
  const d = new Date();
  d.setMonth(d.getMonth() + 2);
  return d.toISOString().split("T")[0];
};

function useDebounce<T>(value: T, delay = 400) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

export function useCostCalculator() {
  const [form, setForm] = useState<CostCalculatorDto>({
    SessionId: sessionStorage.getItem("sessionId") ?? undefined,
    HasProvider: true,
    CurrentProviderPrice: 3.0,
    EstimatedMontlyLookups: 5000,
    CurrentProviderRenewalDate: defaultExpiryDate(),
    EmailAddress: "",
    PhoneNumber: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const abortRef = useRef<AbortController | null>(null);

  // ✅ Debounce ONLY contact fields
  const debouncedEmail = useDebounce(form.EmailAddress, 400);
  const debouncedPhone = useDebounce(form.PhoneNumber, 400);

  // 🔹 Calculate EAF CPL
  const eafCostPerLookup = useMemo(() => {
    const lookups = form.EstimatedMontlyLookups ?? 0;
    return (
      lookupRanges.find(
        (r) => lookups >= r.min && lookups <= r.max
      )?.price ?? 1.3
    );
  }, [form.EstimatedMontlyLookups]);

  // 🔹 Calculations (unchanged logic)
  useEffect(() => {
    const lookups = form.EstimatedMontlyLookups ?? 0;
    const providerCpl = form.CurrentProviderPrice ?? 0;

    const providerCost = (providerCpl * lookups) / 100;
    const eafCost = (eafCostPerLookup * lookups) / 100;
    const saving = providerCost - eafCost;

    setForm((prev) => {
      const next = {
        ...prev,
        CurrentProvidersEstimatedCost: providerCost,
        OurPrice: eafCost,
        TotalEstimatedSavingsWithEAFPerMonth: saving > 0 ? saving : 0,
        TotalEstimatedSavingsWithEAFPerAnnum: saving > 0 ? saving * 12 : 0,
      };

      // 🔥 prevent unnecessary re-renders
      if (
        prev.CurrentProvidersEstimatedCost ===
          next.CurrentProvidersEstimatedCost &&
        prev.OurPrice === next.OurPrice &&
        prev.TotalEstimatedSavingsWithEAFPerMonth ===
          next.TotalEstimatedSavingsWithEAFPerMonth &&
        prev.TotalEstimatedSavingsWithEAFPerAnnum ===
          next.TotalEstimatedSavingsWithEAFPerAnnum
      ) {
        return prev;
      }

      return next;
    });
  }, [
    form.EstimatedMontlyLookups,
    form.CurrentProviderPrice,
    eafCostPerLookup,
  ]);

  // 🔹 Instant log for lookup/provider changes ONLY
  useEffect(() => {
    if (!form.EstimatedMontlyLookups) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const log = async () => {
      try {
        const result = await apiPost<{
          sessionId: string;
          id: number;
        }>(
          "/activity/addCostCalculatorLog",
          {
            EstimatedMontlyLookups: form.EstimatedMontlyLookups,
            CurrentProviderPrice: form.CurrentProviderPrice,
            HasProvider: form.HasProvider,
            CurrentProviderRenewalDate:
              form.CurrentProviderRenewalDate,
            SessionId:
              sessionStorage.getItem("sessionId") ?? undefined,
          },
          { signal: controller.signal }
        );

        if (result.sessionId) {
          sessionStorage.setItem("sessionId", result.sessionId);
        }

        setForm((prev) =>
          prev.Id === result.id ? prev : { ...prev, Id: result.id }
        );
      } catch {}
    };

    log();

    return () => controller.abort();
  }, [
    form.EstimatedMontlyLookups,
    form.CurrentProviderPrice,
    form.HasProvider,
    form.CurrentProviderRenewalDate,
  ]);

  // 🔹 Debounced log for Email + Phone ONLY
  useEffect(() => {
    if (!debouncedEmail && !debouncedPhone) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const logContact = async () => {
      try {
        const result = await apiPost<{
          sessionId: string;
          id: number;
        }>(
          "/activity/addCostCalculatorLog",
          {
            EmailAddress: debouncedEmail,
            PhoneNumber: debouncedPhone,
            SessionId:
              sessionStorage.getItem("sessionId") ?? undefined,
          },
          { signal: controller.signal }
        );

        if (result.sessionId) {
          sessionStorage.setItem("sessionId", result.sessionId);
        }

        setForm((prev) =>
          prev.Id === result.id ? prev : { ...prev, Id: result.id }
        );
      } catch {}
    };

    logContact();

    return () => controller.abort();
  }, [debouncedEmail, debouncedPhone]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    setForm((prev) => ({
      ...prev,
      Id: name === "EstimatedMontlyLookups" ? undefined : prev.Id,
      [name]:
        type === "number"
          ? Number(value)
          : name === "HasProvider"
          ? value === "true"
          : value,
    }));
  };

  const submit = async (showMessage = false) => {
    setLoading(true);
    setError(null);

    try {
      const result = await apiPost<{
        sessionId: string;
        id: number;
      }>("/activity/addCostCalculatorLog", {
        ...form,
        SessionId: sessionStorage.getItem("sessionId") ?? undefined,
      });

      if (result.sessionId) {
        sessionStorage.setItem("sessionId", result.sessionId);
      }

      setForm((prev) => ({
        ...prev,
        Id: result.id,
      }));

      if (showMessage) setSuccess(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resetStatus = () => {
    setError(null);
    setSuccess(false);
  };

  return {
    form,
    loading,
    error,
    success,
    handleChange,
    submit,
    resetStatus,
  };
}